import { type Upload, uploadManager } from '@jwplayer/companion-gateway';
import type { WuiPersistentModalDefaultProps } from '@jwplayer/wui-components/dist/types/modules/overlays/types';
import { WuiBox, WuiButton, getWuiOverlaysApi } from '@jwplayer/wui-react';
import { useEffect, useMemo } from 'react';

import { UploadTile } from '/component/upload-tile';
import { bytesToSize } from '/module/format';
import { useCurrentSiteIdOrThrow } from '/module/sites/hooks';

import css from './style.module.scss';
import { UPLOAD_TYPE_READABLE_NAME, type UploadType, pluralize, useAllUploadsState, useUploadState } from './util';

export const UploadModal = ({ uploadType }: { uploadType: UploadType }) => {
  // When we have specific uploads, (create and) show the modal element
  useEffect(() => {
    const unsub = uploadManager.subscribe(
      (s) => s.uploads.filter((upload) => upload.type === uploadType).length,
      async (mediaUploadsLength) => {
        if (!mediaUploadsLength) {
          return;
        }

        await getWuiOverlaysApi().PersistentModal.createAndOpen('upload-manager', (props) => (
          <UploadManagerModal uploadType={uploadType} {...props} />
        ));
      },
    );

    return () => {
      unsub();
    };
  }, [uploadType]);

  return null;
};

const UploadManagerModal = ({
  api,
  uploadType,
}: {
  api: WuiPersistentModalDefaultProps['api'];
  uploadType: UploadType;
}) => {
  const { uploads: allUploads, progress } = useAllUploadsState();
  const uploadTypeName = UPLOAD_TYPE_READABLE_NAME[uploadType];

  // Filter for specific uploads
  const uploads = useMemo(() => {
    return allUploads.filter((upload) => upload.type === uploadType);
  }, [allUploads, uploadType]);
  const uploadsCount = uploads.length;

  const activeUploadsCount = useMemo(() => {
    return uploads.filter((upload) => upload.getState().state === 'active').length;
  }, [uploads]);

  const finishedUploadsCount = useMemo(() => {
    return uploads.filter((upload) => upload.getState().state === 'finished').length;
  }, [uploads]);

  const failedUploadsCount = useMemo(() => {
    return uploads.filter((upload) => upload.getState().state === 'failed').length;
  }, [uploads]);

  /**
   * Track uploads progress
   */
  useEffect(() => {
    api.waitForElement().then((modalElm: HTMLWuiPersistentModalElement) => {
      if (finishedUploadsCount > 0 && finishedUploadsCount >= uploadsCount) {
        modalElm.header = `${uploadsCount} ${uploadTypeName} ${pluralize(uploadsCount, 'item')} uploaded`;
        modalElm.closeButtonDisabled = false;
        modalElm.headerPrefix = {
          icon: 'check',
        };
      } else if (failedUploadsCount > 0 && failedUploadsCount < uploadsCount) {
        modalElm.header = `${finishedUploadsCount} of ${uploadsCount} ${uploadTypeName} ${pluralize(uploadsCount, 'item')} uploaded`;
        modalElm.closeButtonDisabled = false;
        modalElm.headerPrefix = {
          icon: 'alert-triangle',
        };
      } else if (failedUploadsCount > 0 && failedUploadsCount === uploadsCount) {
        modalElm.header = `Failed to add ${uploadTypeName} items`;
        modalElm.closeButtonDisabled = false;
        modalElm.headerPrefix = {
          icon: 'alert-circle-filled',
        };
      } else {
        modalElm.header =
          uploadsCount === 0
            ? ''
            : activeUploadsCount === 0
              ? `Preparing to upload ${uploadTypeName} items`
              : `Uploading ${activeUploadsCount + finishedUploadsCount} of ${uploadsCount} ${uploadTypeName} ${pluralize(uploadsCount, 'item')}`;
        modalElm.closeButtonDisabled = true;
        modalElm.headerPrefix = {
          progress,
        };
      }
    });
  }, [api, uploadsCount, uploadTypeName, activeUploadsCount, finishedUploadsCount, failedUploadsCount, progress]);

  /**
   * Show the browser warning prompt when the user decides to close/reload the tab while there is an ongoing upload
   * Prompt component only triggers when navigating back/to another route
   */
  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (finishedUploadsCount < uploadsCount) {
        event.preventDefault();
      }
    };

    window.addEventListener('beforeunload', onBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', onBeforeUnload);
    };
  }, [finishedUploadsCount, uploadsCount]);

  /**
   * If the user closes the modal or clears all uploads, all uploads are cleared from the list
   */
  useEffect(() => {
    const modal = api.getElement();

    const clearUploads = (e: CustomEvent<string>) => {
      if ((e as CustomEvent).detail === 'closed') {
        removeUploads();
      }
    };

    modal?.addEventListener('displayChanged', clearUploads);

    return () => {
      modal?.removeEventListener('displayChanged', clearUploads);
    };
  });

  /**
   * Remove all uploads from the persistent modal
   */
  const removeUploads = () => {
    if (uploadsCount > 0) {
      uploads.forEach((upload) => {
        upload.remove();
      });
    }
  };

  /**
   * Close persistent modal
   */
  const closeModal = async () => {
    (await api.waitForElement()).setDisplay('closed');
  };

  /**
   * Cancel unfinished uploads
   */
  const onCancelAllUploads = async () => {
    uploads.forEach((upload) => {
      if (upload.getState().state !== 'finished') {
        upload.abort();
        upload.remove();
      }
    });

    const abortedUploadsCount = uploads.filter((upload) => upload.getState().state === 'aborted').length;

    // All uploads were cancelled, close the modal
    if (abortedUploadsCount === uploadsCount) {
      closeModal();
    }
  };

  /**
   * Clear all uploads
   */
  const onClearAllUploads = () => {
    // When we close the modal, we automatically clear uploads
    closeModal();
  };

  if (uploadsCount === 0 || (activeUploadsCount === 0 && finishedUploadsCount === 0)) {
    return null;
  }

  return (
    <>
      <WuiBox gutter="xs" slot="body" className={css.container}>
        {uploads.map((upload: Upload) => {
          return (
            <UploadItem
              key={upload.index}
              upload={upload}
              onRemove={() => {
                // If the last upload was removed, close the modal
                if (uploadsCount === 1) {
                  closeModal();
                }
              }}
            />
          );
        })}
      </WuiBox>

      {uploadsCount > 1 && (
        <WuiBox orientation="horizontal" justify="end" slot="actions">
          <WuiButton
            icon="x"
            type="tertiary"
            intent={!activeUploadsCount ? undefined : 'error'}
            label={!activeUploadsCount ? 'Clear all' : 'Cancel all'}
            data-test={!activeUploadsCount ? 'clear-all-uploads' : 'cancel-all-uploads'}
            onClick={!activeUploadsCount ? onClearAllUploads : onCancelAllUploads}
          />
        </WuiBox>
      )}
    </>
  );
};

const UploadItem = ({ upload, onRemove }: { upload: Upload; onRemove: () => void }) => {
  const siteId = useCurrentSiteIdOrThrow();

  const { asset, state, progress } = useUploadState(upload);

  const mediaId = asset?.id;

  const isFinished = state === 'finished';
  const isFailed = state === 'failed';
  const isActive = state === 'active';

  const fileSize = 'file' in upload && upload.file instanceof File ? upload.file.size : undefined;
  const showFileSize = fileSize && isFinished && !isFailed;

  const title = useMemo(() => {
    const defaultTitle = 'Untitled media';

    if (asset?.type === 'media') {
      return asset.metadata.title || defaultTitle;
    }
    if (asset?.type === 'text_track') {
      return asset.metadata.label || defaultTitle;
    }
    return upload.metadata.titleFromFilename;
  }, [upload, asset]);

  return (
    <UploadTile
      id={mediaId}
      siteId={siteId}
      type="Media"
      label={title}
      loading={isActive}
      progress={progress}
      removeBtn={{
        type: 'remove',
        onClick() {
          upload.abort();
          upload.remove();

          onRemove();
        },
      }}
      failed={isFailed}
      size={showFileSize ? bytesToSize(fileSize) : undefined}
    />
  );
};
